import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  Send,
  ChevronLeft,
  FileText,
  Download,
  Eye,
  Clock,
  AlertCircle,
  CheckCircle,
  Loader,
  RefreshCw,
  ThumbsUp,
  User,
  Building,
  MoreHorizontal,
} from 'lucide-react';
import useSWR from 'swr';
import {
  getTicketByIdUrl,
  fetcher,
  addTicketComment,
  updateTicketStatus,
} from '../services/ApiSupport';
import { useTranslation } from '../../TranslationContext';
import { useAdminInfo } from '../../admin/pages/Dashboard/DashboarEntreprise/hooksUseretApplication/hookAdmin Info';

// Interfaces inchangées
interface TicketComment {
  id: number;
  ticketId: number;
  author: string;
  message: string;
  createdAt: string;
}

interface Ticket {
  id: number;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  type: string;
  priority: string;
  createdBy: string;
  companyId: number;
  updatedAt: string | null;
  response?: {
    content: string;
    type: 'text' | 'pdf';
    pdfUrl?: string;
  };
}

interface TicketResponse {
  isSuccess: boolean;
  data: Ticket & { comments: TicketComment[] };
  errorMessage: string;
}

interface ModalState {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'success' | 'error';
}

const TicketDetail: React.FC = () => {
  const { t } = useTranslation();
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('company') || '';
  const { adminInfo } = useAdminInfo();

  // États
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success',
  });

  // Récupération des données du ticket
  const {
    data: ticketResponse,
    error: ticketError,
    mutate: mutateTicket,
  } = useSWR<TicketResponse>(
    companyId && ticketId ? getTicketByIdUrl(ticketId) : null,
    fetcher,
    {
      revalidateOnFocus: false,
      refreshInterval: 30000,
    }
  );

  const ticket = ticketResponse?.data;
  const comments = ticket?.comments || [];

  // Gestion du clic en dehors du dropdown
  useEffect(() => {
    const handleClickOutside = () => {
      if (dropdownOpen) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  // Indicateur de succès après chargement
  useEffect(() => {
    if (ticketResponse?.isSuccess) {
      setShowSuccess(true);
      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [ticketResponse]);

  // Formater la date
  const formatDate = (dateString: string | number | Date) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      return String(dateString);
    }
  };

  // Rafraîchir les données
  const handleRefresh = () => {
    mutateTicket();
  };

  // Mettre à jour le statut du ticket
  const handleUpdateStatus = async (newStatus: string) => {
    if (!companyId || !ticketId || !ticket) return;
    try {
      setIsUpdatingStatus(true);
      const status = newStatus.toLowerCase() === 'open' ? 'open' : 'Closed';
      await updateTicketStatus(companyId, ticketId.toString(), status);
      await mutateTicket();
      setDropdownOpen(false);
      setModal({
        isOpen: true,
        title: status === 'open' ? t('ticket_reopened') : t('ticket_closed_success'),
        message:
          status === 'open'
            ? t('ticket_reopened_message')
            : t('ticket_closed_success_message'),
        type: 'success',
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      setModal({
        isOpen: true,
        title: t('ticket_status_update_failed'),
        message: t('ticket_status_update_failed_message'),
        type: 'error',
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Ajouter un commentaire
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      setIsSubmitting(true);
      if (!companyId || !ticketId) throw new Error('Identifiants manquants');
      await addTicketComment(companyId, ticketId, {
        author: adminInfo?.email || 'Utilisateur',
        message: comment.trim(),
      });
      setComment('');
      mutateTicket();
    } catch (error) {
      console.error("Erreur lors de l'ajout du commentaire:", error);
      setModal({
        isOpen: true,
        title: t('comment_submission_failed'),
        message: t('comment_submission_failed_message'),
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Couleurs pour priorité et type
  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-rose-100 text-rose-800';
      case 'medium':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-emerald-100 text-emerald-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'technical':
        return 'bg-purple-100 text-purple-800';
      case 'billing':
        return 'bg-blue-100 text-blue-800';
      case 'feature':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Télécharger PDF
  const handleDownloadPdf = (url?: string) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = `ticket-${ticketId}-response.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Gestion du retour à la page Support avec ancrage
  const handleBackToSupport = () => {
    navigate('/support', { state: { scrollToTickets: true } });
  };

  return (
    <div className="max-w-5xl mx-auto py-8">
      {/* Modal pour notifications */}
      {modal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3
              className={`text-lg font-semibold ${
                modal.type === 'success' ? 'text-green-600' : 'text-rose-600'
              }`}
            >
              {modal.title}
            </h3>
            <p className="text-slate-600 mt-2">{modal.message}</p>
            <button
              onClick={() => setModal({ ...modal, isOpen: false })}
              className="mt-4 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors duration-200"
            >
              {t('close')}
            </button>
          </div>
        </div>
      )}

      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button
            onClick={handleBackToSupport}
            className="mr-4 p-2 rounded-full hover:bg-slate-100 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h2 className="text-2xl font-bold text-slate-800">{t('ticket_details')}</h2>
        </div>
        <div className="flex items-center">
          {showSuccess && (
            <div className="flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-lg mr-4 transition-opacity duration-300">
              <ThumbsUp className="w-4 h-4 mr-2" />
              <span className="text-sm">{t('data_refreshed')}</span>
            </div>
          )}
          <button
            onClick={handleRefresh}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors duration-200"
            title={t('refresh_data')}
          >
            <RefreshCw className="w-5 h-5 text-slate-600" />
          </button>
        </div>
      </div>

      {/* Gestion des erreurs et chargement */}
      {ticketError ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <AlertCircle className="w-16 h-16 text-rose-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('error_loading_ticket')}</h3>
          <p className="text-slate-600 mb-6">{t('error_loading_ticket_message')}</p>
          <button
            onClick={handleBackToSupport}
            className="px-6 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 transition-colors duration-200"
          >
            {t('back_to_tickets')}
          </button>
        </div>
      ) : !ticket ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <Loader className="w-16 h-16 text-sky-500 mx-auto mb-4 animate-spin" />
          <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('loading_ticket')}</h3>
          <p className="text-slate-600">{t('please_wait')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Carte du ticket */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
            <div className="flex justify-between mb-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-semibold text-slate-800">{ticket.title}</h3>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      ticket.status?.toLowerCase() === 'open'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {ticket.status?.toLowerCase() === 'open' ? t('status_open') : t('status_resolved')}
                  </span>
                </div>
                <div className="flex items-center text-sm text-slate-500">
                  <Clock className="w-4 h-4 mr-1" />
                  {formatDate(ticket.createdAt)}
                  <span className="mx-2">•</span>
                  <span>#{ticket.id}</span>
                  {ticket.updatedAt && (
                    <>
                      <span className="mx-2">•</span>
                      <span>{t('last_updated')}: {formatDate(ticket.updatedAt)}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                >
                  <MoreHorizontal className="w-5 h-5 text-slate-600" />
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-slate-200">
                    <div className="py-1">
                      {ticket.status?.toLowerCase() === 'open' ? (
                        <button
                          onClick={() => handleUpdateStatus('Closed')}
                          disabled={isUpdatingStatus}
                          className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                        >
                          {isUpdatingStatus ? t('updating_status') : t('close_ticket')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleUpdateStatus('open')}
                          disabled={isUpdatingStatus}
                          className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                        >
                          {isUpdatingStatus ? t('updating_status') : t('reopen_ticket')}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                {ticket.priority === 'high'
                  ? t('priority_high')
                  : ticket.priority === 'medium'
                  ? t('priority_medium')
                  : t('priority_low')}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(ticket.type)}`}>
                {ticket.type === 'technical'
                  ? t('category_technical')
                  : ticket.type === 'billing'
                  ? t('category_billing')
                  : ticket.type === 'feature'
                  ? t('category_feature')
                  : t('category_other')}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6 text-sm text-slate-600">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1 text-slate-400" />
                <span className="font-medium mr-2">{t('created_by')}:</span>
                {ticket.createdBy}
              </div>
              <div className="flex items-center">
                <Building className="w-4 h-4 mr-1 text-slate-400" />
                <span className="font-medium mr-2">{t('company_id')}:</span>
                {ticket.companyId}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-slate-700 mb-2">{t('ticket_content')}</h4>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="whitespace-pre-wrap text-slate-600">{ticket.description}</p>
              </div>
            </div>
          </div>

          {/* Section de discussion */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center">
                <MessageCircle className="w-5 h-5 mr-2 text-sky-500" />
                {t('discussion')}
                {comments.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-sky-100 text-sky-600 text-xs rounded-full">
                    {comments.length}
                  </span>
                )}
              </h3>
            </div>

            {ticket.response && (
              <div className="border-b border-slate-100 p-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <MessageCircle className="w-4 h-4 text-sky-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-slate-700">{t('support_team')}</span>
                    </div>
                    {ticket.response.type === 'text' ? (
                      <div className="bg-sky-50 rounded-lg p-3 text-slate-700">
                        <p className="whitespace-pre-wrap">{ticket.response.content}</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
                        <div className="flex items-center">
                          <FileText className="h-8 w-8 text-slate-400 mr-3" />
                          <div>
                            <span className="block text-slate-800 font-medium">{ticket.response.content}</span>
                            <span className="text-xs text-slate-500">{t('pdf_document')}</span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            className="inline-flex items-center px-3 py-1 bg-sky-100 text-sky-600 rounded-md text-sm hover:bg-sky-200 transition-colors duration-200"
                            onClick={() => window.open(ticket.response?.pdfUrl, '_blank')}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {t('view')}
                          </button>
                          <button
                            className="inline-flex items-center px-3 py-1 bg-sky-500 text-white rounded-md text-sm hover:bg-sky-600 transition-colors duration-200"
                            onClick={() => handleDownloadPdf(ticket.response?.pdfUrl)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            {t('download')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="p-4">
              {comments.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-lg">
                  <MessageCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-slate-600">{t('no_comments_yet')}</p>
                </div>
              ) : (
                <div className="space-y-4 mb-4 max-h-96 overflow-y-auto pr-2">
                  {comments.map((comment) => (
                    <div key={comment.id} className="flex items-start">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                          comment.author === adminInfo?.email ? 'bg-indigo-100' : 'bg-slate-100'
                        }`}
                      >
                        <User
                          className={`w-4 h-4 ${
                            comment.author === adminInfo?.email ? 'text-indigo-500' : 'text-slate-500'
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-slate-700">{comment.author}</span>
                          <span className="text-xs text-slate-500">{formatDate(comment.createdAt)}</span>
                        </div>
                        <div
                          className={`rounded-lg p-3 ${
                            comment.author === adminInfo?.email ? 'bg-indigo-50 text-slate-700' : 'bg-slate-50 text-slate-700'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{comment.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {ticket.status?.toLowerCase() === 'open' && (
              <div className="p-4 border-t border-slate-200">
                <form onSubmit={handleSubmitComment} className="flex items-end">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <User className="w-4 h-4 text-indigo-500" />
                  </div>
                  <div className="flex-1 relative">
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder={t('write_your_message')}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors duration-200 resize-none"
                      rows={2}
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting || !comment.trim()}
                      className={`absolute right-2 bottom-2 p-1 rounded-full ${
                        comment.trim() ? 'bg-sky-500 hover:bg-sky-600' : 'bg-slate-300'
                      } text-white transition-colors duration-200`}
                    >
                      {isSubmitting ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {ticket.status?.toLowerCase() !== 'open' && (
              <div className="p-4 border-t border-slate-200 bg-slate-50 text-center text-slate-600">
                <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-2" />
                <p>{t('ticket_closed_message')}</p>
                <button
                  onClick={() => handleUpdateStatus('open')}
                  disabled={isUpdatingStatus}
                  className="mt-2 px-4 py-1 bg-slate-200 hover:bg-slate-300 rounded-md text-sm font-medium transition-colors duration-200"
                >
                  {isUpdatingStatus ? (
                    <span className="flex items-center justify-center">
                      <Loader className="w-3 h-3 animate-spin mr-1" />
                      {t('updating_status')}
                    </span>
                  ) : (
                    t('reopen_ticket')
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetail;

// import React, { useState, useEffect } from 'react';
// import { useParams, useNavigate } from 'react-router-dom';
// import {
//   MessageCircle,
//   Send,
//   ChevronLeft,
//   FileText,
//   Download,
//   Eye,
//   Clock,
//   AlertCircle,
//   CheckCircle,
//   Loader,
//   RefreshCw,
//   ThumbsUp,
//   User,
//   Building,
//   MoreHorizontal,
// } from 'lucide-react';
// import useSWR from 'swr';
// import {
//   getTicketByIdUrl,
//   fetcher,
//   addTicketComment,
//   updateTicketStatus,
// } from '../services/ApiSupport';
// import { useTranslation } from '../../TranslationContext';
// import { useAdminInfo } from '../../admin/pages/Dashboard/DashboarEntreprise/hooksUseretApplication/hookAdmin Info';

// /**
//  * Interface pour un commentaire de ticket
//  */
// interface TicketComment {
//   id: number;
//   ticketId: number;
//   author: string;
//   message: string;
//   createdAt: string;
// }

// /**
//  * Interface pour un ticket
//  */
// interface Ticket {
//   id: number;
//   title: string;
//   description: string;
//   status: string;
//   createdAt: string;
//   type: string;
//   priority: string;
//   createdBy: string;
//   companyId: number;
//   updatedAt: string | null;
//   response?: {
//     content: string;
//     type: 'text' | 'pdf';
//     pdfUrl?: string;
//   };
// }

// /**
//  * Interface pour la réponse API d'un ticket
//  */
// interface TicketResponse {
//   isSuccess: boolean;
//   data: Ticket & { comments: TicketComment[] }; // Inclut les commentaires dans la réponse
//   errorMessage: string;
// }

// /**
//  * Interface pour l'état de la modal
//  */
// interface ModalState {
//   isOpen: boolean;
//   title: string;
//   message: string;
//   type: 'success' | 'error';
// }

// /**
//  * Composant de détail d'un ticket
//  */
// const TicketDetail: React.FC = () => {
//   // Hooks et états
//   const { t } = useTranslation();
//   const { ticketId } = useParams<{ ticketId: string }>();
//   const navigate = useNavigate();
//   const companyId = localStorage.getItem('company') || '';
//   const { adminInfo } = useAdminInfo();
  
//   // États pour la gestion des formulaires et des modals
//   const [comment, setComment] = useState('');
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
//   const [showSuccess, setShowSuccess] = useState(false);
//   const [dropdownOpen, setDropdownOpen] = useState(false);
//   const [modal, setModal] = useState<ModalState>({
//     isOpen: false,
//     title: '',
//     message: '',
//     type: 'success',
//   });

//   // Récupération des données du ticket avec SWR
//   const { 
//     data: ticketResponse, 
//     error: ticketError, 
//     mutate: mutateTicket 
//   } = useSWR<TicketResponse>(
//     companyId && ticketId ? getTicketByIdUrl( ticketId) : null,
//     fetcher,
//     {
//       revalidateOnFocus: false,
//       refreshInterval: 30000, // Actualisation toutes les 30 secondes
//     }
//   );

//   // Extraction des données pour faciliter l'accès
//   const ticket = ticketResponse?.data;
//   const comments = ticket?.comments || [];

//   // Effet pour fermer le dropdown quand on clique en dehors
//   useEffect(() => {
//     // const handleClickOutside = (event: MouseEvent) => {
      
//       const handleClickOutside = () => {
//       if (dropdownOpen) {
//         setDropdownOpen(false);
//       }
//     };
    
//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, [dropdownOpen]);

//   // Effet pour afficher l'indicateur de succès après chargement
//   useEffect(() => {
//     if (ticketResponse?.isSuccess) {
//       setShowSuccess(true);
//       const timer = setTimeout(() => setShowSuccess(false), 3000);
//       return () => clearTimeout(timer);
//     }
//   }, [ticketResponse]);

//   /**
//    * Formate une date en format local français
//    */
//   const formatDate = (dateString: string | number | Date) => {
//     if (!dateString) return '';
//     try {
//       const date = new Date(dateString);
//       return new Intl.DateTimeFormat('fr-FR', {
//         day: '2-digit',
//         month: '2-digit',
//         year: 'numeric',
//         hour: '2-digit',
//         minute: '2-digit',
//       }).format(date);
//     } catch (error) {
//       return String(dateString);
//     }
//   };

//   /**
//    * Actualise manuellement les données du ticket
//    */
//   const handleRefresh = () => {
//     mutateTicket();
//   };

//   /**
//    * Met à jour le statut d'un ticket (ouvert/fermé)
//    */
//   const handleUpdateStatus = async (newStatus: string) => {
//     if (!companyId || !ticketId || !ticket) return;
//     try {
//       setIsUpdatingStatus(true);
      
//       // Utiliser la même convention que TicketSystem
//       if (newStatus.toLowerCase() === 'open') {
//         await updateTicketStatus(companyId, ticketId.toString(), 'open');
//       } else {
//         await updateTicketStatus(companyId, ticketId.toString(), 'Closed');
//       }
      
//       // Attendre la fin de la mise à jour avant de continuer
//       await mutateTicket();
//       setDropdownOpen(false);
//       setModal({
//         isOpen: true,
//         title: newStatus.toLowerCase() === 'open' ? t('ticket_reopened') : t('ticket_closed_success'),
//         message: newStatus.toLowerCase() === 'open' ? t('ticket_reopened_message') : t('ticket_closed_success_message'),
//         type: 'success',
//       });
//     } catch (error) {
//       console.error('Erreur lors de la mise à jour du statut:', error);
//       setModal({
//         isOpen: true,
//         title: t('ticket_status_update_failed'),
//         message: t('ticket_status_update_failed_message'),
//         type: 'error',
//       });
//     } finally {
//       setIsUpdatingStatus(false);
//     }
//   };
//   /**
//    * Ajoute un commentaire au ticket
//    */
//   const handleSubmitComment = async (e: { preventDefault: () => void; }) => {
//     e.preventDefault();
//     if (!comment.trim()) return;
//     try {
//       setIsSubmitting(true);
//       if (!companyId || !ticketId) throw new Error('Identifiants manquants');
//       await addTicketComment(companyId, ticketId, {
//         author: adminInfo?.email || 'Utilisateur',
//         message: comment.trim(),
//       });
//       setComment('');
//       mutateTicket(); // Actualise le ticket après l'ajout du commentaire
//     } catch (error) {
//       console.error("Erreur lors de l'ajout du commentaire:", error);
//       setModal({
//         isOpen: true,
//         title: t('comment_submission_failed'),
//         message: t('comment_submission_failed_message'),
//         type: 'error',
//       });
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   /**
//    * Renvoie la classe CSS pour la couleur de priorité
//    */
//   const getPriorityColor = (priority: string) => {
//     switch (priority?.toLowerCase()) {
//       case 'high':
//         return 'bg-rose-100 text-rose-800';
//       case 'medium':
//         return 'bg-amber-100 text-amber-800';
//       default:
//         return 'bg-emerald-100 text-emerald-800';
//     }
//   };

//   /**
//    * Renvoie la classe CSS pour la couleur de type
//    */
//   const getTypeColor = (type: string) => {
//     switch (type?.toLowerCase()) {
//       case 'technical':
//         return 'bg-purple-100 text-purple-800';
//       case 'billing':
//         return 'bg-blue-100 text-blue-800';
//       case 'feature':
//         return 'bg-indigo-100 text-indigo-800';
//       default:
//         return 'bg-gray-100 text-gray-800';
//     }
//   };

//   /**
//    * Télécharge un fichier PDF
//    */
//   const handleDownloadPdf = (url?: string) => {
//     if (!url) return;
    
//     const link = document.createElement('a');
//     link.href = url;
//     link.target = '_blank';
//     link.download = `ticket-${ticketId}-response.pdf`;
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   };

//   return (
//     <div className="max-w-5xl mx-auto">
//       {/* Modal pour les notifications */}
//       {modal.isOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
//             <h3
//               className={`text-lg font-semibold ${
//                 modal.type === 'success' ? 'text-green-600' : 'text-rose-600'
//               }`}
//             >
//               {modal.title}
//             </h3>
//             <p className="text-slate-600 mt-2">{modal.message}</p>
//             <button
//               onClick={() => setModal({ ...modal, isOpen: false })}
//               className="mt-4 px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors duration-200"
//             >
//               {t('close')}
//             </button>
//           </div>
//         </div>
//       )}

//       {/* En-tête avec bouton retour et titre */}
//       <div className="flex justify-between items-center mb-6">
//         <div className="flex items-center">
//           <button
//             onClick={() => navigate('/support')}
//             className="mr-4 p-2 rounded-full hover:bg-slate-100 transition-colors duration-200"
//           >
//             <ChevronLeft className="w-5 h-5 text-slate-600" />
//           </button>
//           <h2 className="text-2xl font-bold text-slate-800">{t('ticket_details')}</h2>
//         </div>
//         <div className="flex items-center">
//           {/* Indicateur de rafraîchissement */}
//           {showSuccess && (
//             <div className="flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-lg mr-4 transition-opacity duration-300">
//               <ThumbsUp className="w-4 h-4 mr-2" />
//               <span className="text-sm">{t('data_refreshed')}</span>
//             </div>
//           )}
//           <button
//             onClick={handleRefresh}
//             className="p-2 rounded-full hover:bg-slate-100 transition-colors duration-200"
//             title={t('refresh_data')}
//           >
//             <RefreshCw className="w-5 h-5 text-slate-600" />
//           </button>
//         </div>
//       </div>

//       {/* Affichage en cas d'erreur ou chargement */}
//       {ticketError ? (
//         <div className="bg-white rounded-xl shadow-sm p-8 text-center">
//           <AlertCircle className="w-16 h-16 text-rose-500 mx-auto mb-4" />
//           <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('error_loading_ticket')}</h3>
//           <p className="text-slate-600 mb-6">{t('error_loading_ticket_message')}</p>
//           <button
//             onClick={() => navigate('/support')}
//             className="px-6 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 transition-colors duration-200"
//           >
//             {t('back_to_tickets')}
//           </button>
//         </div>
//       ) : !ticket ? (
//         <div className="bg-white rounded-xl shadow-sm p-8 text-center">
//           <Loader className="w-16 h-16 text-sky-500 mx-auto mb-4 animate-spin" />
//           <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('loading_ticket')}</h3>
//           <p className="text-slate-600">{t('please_wait')}</p>
//         </div>
//       ) : (
//         <div className="space-y-6">
//           {/* Carte du ticket avec informations principales */}
//           <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
//             <div className="flex justify-between mb-4">
//               <div className="space-y-1">
//                 <div className="flex items-center gap-2">
//                   <h3 className="text-xl font-semibold text-slate-800">{ticket.title}</h3>
//                   <span
//                     className={`px-2 py-1 rounded-full text-xs font-medium ${
//                       ticket.status?.toLowerCase() === 'open'
//                         ? 'bg-green-100 text-green-700'
//                         : 'bg-slate-100 text-slate-700'
//                     }`}
//                   >
//                     {ticket.status?.toLowerCase() === 'open' ? t('status_open') : t('status_resolved')}
//                   </span>
//                 </div>
//                 <div className="flex items-center text-sm text-slate-500">
//                   <Clock className="w-4 h-4 mr-1" />
//                   {formatDate(ticket.createdAt)}
//                   <span className="mx-2">•</span>
//                   <span>#{ticket.id}</span>
//                   {ticket.updatedAt && (
//                     <>
//                       <span className="mx-2">•</span>
//                       <span>{t('last_updated')}: {formatDate(ticket.updatedAt)}</span>
//                     </>
//                   )}
//                 </div>
//               </div>
              
//               {/* Menu déroulant pour les actions sur le ticket */}
//               <div className="relative">
//                 <button 
//                   onClick={() => setDropdownOpen(!dropdownOpen)}
//                   className="p-2 rounded-full hover:bg-slate-100 transition-colors"
//                 >
//                   <MoreHorizontal className="w-5 h-5 text-slate-600" />
//                 </button>
                
//                 {dropdownOpen && (
//                   <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-slate-200">
//                     <div className="py-1">
//                     {ticket.status?.toLowerCase() === 'open' ? (
//   <button
//     onClick={() => handleUpdateStatus('Closed')}
//     disabled={isUpdatingStatus}
//     className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
//   >
//     {isUpdatingStatus ? t('updating_status') : t('close_ticket')}
//   </button>
// ) : (
//   <button
//     onClick={() => handleUpdateStatus('open')}
//     disabled={isUpdatingStatus}
//     className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
//   >
//     {isUpdatingStatus ? t('updating_status') : t('reopen_ticket')}
//   </button>
// )}
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>

//             {/* Tags de priorité et type */}
//             <div className="flex gap-2 mb-4">
//               <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
//                 {ticket.priority === 'high'
//                   ? t('priority_high')
//                   : ticket.priority === 'medium'
//                   ? t('priority_medium')
//                   : t('priority_low')}
//               </span>
//               <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(ticket.type)}`}>
//                 {ticket.type === 'technical'
//                   ? t('category_technical')
//                   : ticket.type === 'billing'
//                   ? t('category_billing')
//                   : ticket.type === 'feature'
//                   ? t('category_feature')
//                   : t('category_other')}
//               </span>
//             </div>
            
//             {/* Informations sur l'utilisateur et l'entreprise */}
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6 text-sm text-slate-600">
//               <div className="flex items-center">
//                 <User className="w-4 h-4 mr-1 text-slate-400" />
//                 <span className="font-medium mr-2">{t('created_by')}:</span>
//                 {ticket.createdBy}
//               </div>
//               <div className="flex items-center">
//                 <Building className="w-4 h-4 mr-1 text-slate-400" />
//                 <span className="font-medium mr-2">{t('company_id')}:</span>
//                 {ticket.companyId}
//               </div>
//             </div>

//             {/* Description du ticket */}
//             <div>
//               <h4 className="font-medium text-slate-700 mb-2">{t('ticket_content')}</h4>
//               <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
//                 <p className="whitespace-pre-wrap text-slate-600">{ticket.description}</p>
//               </div>
//             </div>
//           </div>

//           {/* Section de discussion */}
//           <div className="bg-white rounded-xl shadow-sm border border-slate-200">
//             <div className="p-4 border-b border-slate-200">
//               <h3 className="text-lg font-semibold text-slate-800 flex items-center">
//                 <MessageCircle className="w-5 h-5 mr-2 text-sky-500" />
//                 {t('discussion')} 
//                 {comments.length > 0 && (
//                   <span className="ml-2 px-2 py-0.5 bg-sky-100 text-sky-600 text-xs rounded-full">
//                     {comments.length}
//                   </span>
//                 )}
//               </h3>
//             </div>

//             {/* Réponse du support si disponible */}
//             {ticket.response && (
//               <div className="border-b border-slate-100 p-4">
//                 <div className="flex items-start">
//                   <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center mr-3 flex-shrink-0">
//                     <MessageCircle className="w-4 h-4 text-sky-500" />
//                   </div>
//                   <div className="flex-1">
//                     <div className="flex justify-between items-center mb-1">
//                       <span className="font-medium text-slate-700">{t('support_team')}</span>
//                     </div>
                    
//                     {/* Affichage selon le type de réponse (texte ou PDF) */}
//                     {ticket.response.type === 'text' ? (
//                       <div className="bg-sky-50 rounded-lg p-3 text-slate-700">
//                         <p className="whitespace-pre-wrap">{ticket.response.content}</p>
//                       </div>
//                     ) : (
//                       <div className="flex items-center justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
//                         <div className="flex items-center">
//                           <FileText className="h-8 w-8 text-slate-400 mr-3" />
//                           <div>
//                             <span className="block text-slate-800 font-medium">{ticket.response.content}</span>
//                             <span className="text-xs text-slate-500">{t('pdf_document')}</span>
//                           </div>
//                         </div>
//                         <div className="flex space-x-2">
//                           <button
//                             className="inline-flex items-center px-3 py-1 bg-sky-100 text-sky-600 rounded-md text-sm hover:bg-sky-200 transition-colors duration-200"
//                             onClick={() => window.open(ticket.response?.pdfUrl, '_blank')}
//                           >
//                             <Eye className="h-4 w-4 mr-1" />
//                             {t('view')}
//                           </button>
//                           <button
//                             className="inline-flex items-center px-3 py-1 bg-sky-500 text-white rounded-md text-sm hover:bg-sky-600 transition-colors duration-200"
//                             onClick={() => handleDownloadPdf(ticket.response?.pdfUrl)}
//                           >
//                             <Download className="h-4 w-4 mr-1" />
//                             {t('download')}
//                           </button>
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             )}

//             {/* Section des commentaires */}
//             <div className="p-4">
//               {/* Affichage des commentaires */}
//               {comments.length === 0 ? (
//                 <div className="text-center py-6 bg-slate-50 rounded-lg">
//                   <MessageCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
//                   <p className="text-slate-600">{t('no_comments_yet')}</p>
//                 </div>
//               ) : (
//                 <div className="space-y-4 mb-4 max-h-96 overflow-y-auto pr-2">
//                   {comments.map((comment) => (
//                     <div key={comment.id} className="flex items-start">
//                       <div 
//                         className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
//                           comment.author === adminInfo?.email 
//                             ? 'bg-indigo-100' 
//                             : 'bg-slate-100'
//                         }`}
//                       >
//                         <User className={`w-4 h-4 ${
//                           comment.author === adminInfo?.email 
//                             ? 'text-indigo-500' 
//                             : 'text-slate-500'
//                         }`} />
//                       </div>
//                       <div className="flex-1">
//                         <div className="flex justify-between items-center mb-1">
//                           <span className="font-medium text-slate-700">{comment.author}</span>
//                           <span className="text-xs text-slate-500">{formatDate(comment.createdAt)}</span>
//                         </div>
//                         <div className={`rounded-lg p-3 ${
//                           comment.author === adminInfo?.email
//                             ? 'bg-indigo-50 text-slate-700'
//                             : 'bg-slate-50 text-slate-700'
//                         }`}>
//                           <p className="whitespace-pre-wrap">{comment.message}</p>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>

//             {/* Formulaire d'ajout de commentaire */}
//             {ticket.status?.toLowerCase() === 'open' && (
//               <div className="p-4 border-t border-slate-200">
//                 <form onSubmit={handleSubmitComment} className="flex items-end">
//                   <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0">
//                     <User className="w-4 h-4 text-indigo-500" />
//                   </div>
//                   <div className="flex-1 relative">
//                     <textarea
//                       value={comment}
//                       onChange={(e) => setComment(e.target.value)}
//                       placeholder={t('write_your_message')}
//                       className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-colors duration-200 resize-none"
//                       rows={2}
//                     />
//                     <button
//                       type="submit"
//                       disabled={isSubmitting || !comment.trim()}
//                       className={`absolute right-2 bottom-2 p-1 rounded-full ${
//                         comment.trim() ? 'bg-sky-500 hover:bg-sky-600' : 'bg-slate-300'
//                       } text-white transition-colors duration-200`}
//                     >
//                       {isSubmitting ? (
//                         <Loader className="w-4 h-4 animate-spin" />
//                       ) : (
//                         <Send className="w-4 h-4" />
//                       )}
//                     </button>
//                   </div>
//                 </form>
//               </div>
//             )}
            
//             {/* Message si le ticket est fermé */}
//             {ticket.status?.toLowerCase() !== 'open' && (
//               <div className="p-4 border-t border-slate-200 bg-slate-50 text-center text-slate-600">
//                 <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-2" />
//                 <p>{t('ticket_closed_message')}</p>
//                 <button
//   onClick={() => handleUpdateStatus('open')}
//   disabled={isUpdatingStatus}
//   className="mt-2 px-4 py-1 bg-slate-200 hover:bg-slate-300 rounded-md text-sm font-medium transition-colors duration-200"
// >
//   {isUpdatingStatus ? (
//     <span className="flex items-center justify-center">
//       <Loader className="w-3 h-3 animate-spin mr-1" />
//       {t('updating_status')}
//     </span>
//   ) : (
//     t('reopen_ticket')
//   )}
// </button>
//               </div>
//             )}
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default TicketDetail;